import { FilterX, MapPin, Package, Search, Sparkles } from 'lucide-react';
import type { TFunction } from 'i18next';
import { Button } from '@ooh/ui/components/button';
import { DataTable } from '@ooh/ui/components/data-table';
import { hideNativeScrollbarClassName } from '@ooh/ui/components/sticky-horizontal-scrollbar';
import { RowDetailLink } from '../../../components/row-detail-link';
import { buildUnitsTabColumns } from './units-tab-columns';
import type { OfferUnitItem } from './units-tab-columns';
import type { SortDir, SortKey } from './units-tab-toolbar';

const UNIT_ROW_TEST_ID = 'offer-units-table-row';

interface UnitsTableContentProps {
  columns: ReturnType<typeof buildUnitsTabColumns>;
  displayItems: OfferUnitItem[];
  onColumnReorder: (fromId: string, toId: string) => void;
  onColumnResizeEnd: (columnId: string, widthPx: number) => void;
  sortBy: SortKey;
  sortDir: SortDir;
  onSort: (key: string) => void;
  offerId: string;
  t: TFunction<'offers'>;
  onScrollContainerElement?: (element: HTMLDivElement | null) => void;
  /** Sticky-header offset in px — see `DataTableProps.stickyHeaderOffsetPx`. */
  stickyHeaderOffsetPx?: number;
}

export function UnitsTableContent({
  columns,
  displayItems,
  onColumnReorder,
  onColumnResizeEnd,
  sortBy,
  sortDir,
  onSort,
  offerId,
  t,
  onScrollContainerElement,
  stickyHeaderOffsetPx,
}: UnitsTableContentProps) {
  return (
    <DataTable
      columns={columns}
      data={displayItems}
      getRowKey={(item) => String(item.id)}
      getRowTestId={() => UNIT_ROW_TEST_ID}
      getRowAttributes={(item) => ({ 'data-unit-id': String(item.id) })}
      onColumnReorder={onColumnReorder}
      onColumnResizeEnd={onColumnResizeEnd}
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={onSort}
      renderRowLink={(item) => (
        <RowDetailLink
          to="/media-units/$id"
          params={{ id: String(item.mediaUnitId) }}
          search={{ offerId }}
          ariaLabel={t('workspace.unitsTab.rowLinkAriaLabel', { id: item.id })}
          testId={`offer-units-row-link-${item.id}`}
        />
      )}
      className="rounded-none border-0"
      stickyHeaderOffsetPx={stickyHeaderOffsetPx}
      onScrollContainerElement={onScrollContainerElement}
      // The native horizontal scrollbar sits at the bottom of this full-height
      // box — off screen until the very end of the list, and covered by the
      // totals footer there. StickyHorizontalScrollbar replaces it.
      scrollContainerClassName={hideNativeScrollbarClassName}
    />
  );
}

interface OfferUnitsEmptyStateProps {
  canEdit: boolean;
  onOpenMapMode?: () => void;
  onPrimaryAction: () => void;
  t: TFunction<'offers'>;
}

export function OfferUnitsEmptyState({
  canEdit,
  onOpenMapMode,
  onPrimaryAction,
  t,
}: OfferUnitsEmptyStateProps) {
  return (
    <div
      className="flex h-full flex-col items-center justify-center px-8 py-24 text-center"
      data-testid="offer-units-empty-state"
    >
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted/60">
        <Package className="h-8 w-8 text-muted-foreground/60" strokeWidth={1.5} />
      </div>
      <h2 className="mb-2 text-[15px] font-semibold">{t('workspace.unitsTab.empty.title')}</h2>
      <p className="mb-8 max-w-sm text-[13px] text-muted-foreground">
        {t('workspace.unitsTab.empty.description')}
      </p>
      {canEdit ? (
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button
            size="sm"
            data-testid="offer-units-empty-primary-action"
            onClick={onPrimaryAction}
          >
            <Search className="h-3.5 w-3.5" />
            {t('workspace.unitsTab.empty.primaryAction')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={onOpenMapMode === undefined}
            onClick={onOpenMapMode}
          >
            <MapPin className="h-3.5 w-3.5" />
            {t('workspace.unitsTab.toolbar.addModes.map')}
          </Button>
          <Button variant="outline" size="sm" disabled>
            <Sparkles className="h-3.5 w-3.5" />
            {t('workspace.unitsTab.toolbar.addModes.ai')}
          </Button>
          <Button variant="outline" size="sm" disabled>
            <Package className="h-3.5 w-3.5" />
            {t('workspace.unitsTab.empty.importPackage')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

interface OfferUnitsNoResultsStateProps {
  /** The offer's true unfiltered total — kept visible even while filtered to nothing. */
  unfilteredTotal: number;
  onClearFilters: () => void;
  t: TFunction<'offers'>;
}

/**
 * Distinct from `OfferUnitsEmptyState`: a filter matching nothing is NOT the
 * empty-offer state that invites adding the first unit (offer-unified-table
 * spec, "A filter matching nothing is not the empty-offer state").
 */
export function OfferUnitsNoResultsState({
  unfilteredTotal,
  onClearFilters,
  t,
}: OfferUnitsNoResultsStateProps) {
  return (
    <div
      className="flex h-full flex-col items-center justify-center px-8 py-24 text-center"
      data-testid="offer-units-no-results-state"
    >
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted/60">
        <FilterX className="h-8 w-8 text-muted-foreground/60" strokeWidth={1.5} />
      </div>
      <h2 className="mb-2 text-base font-semibold">{t('workspace.unitsTab.noResults.title')}</h2>
      <p className="mb-8 max-w-sm text-sm text-muted-foreground">
        {t('workspace.unitsTab.noResults.description', { count: unfilteredTotal })}
      </p>
      <Button size="sm" data-testid="offer-units-clear-filters" onClick={onClearFilters}>
        {t('workspace.unitsTab.noResults.clearFilters')}
      </Button>
    </div>
  );
}

// UnitsTabFooter (the placeholder pricedCount/soldCount/totalPrice footer)
// was retired when the merged table gained real price columns and the
// offer-wide totals footer (`PricingTabTotalsFooter`, server-computed) —
// see units-tab-page.tsx.
